import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Card, Container, Divider, Grid, Header, Icon, List} from 'semantic-ui-react';
import {FilePond, registerPlugin} from 'react-filepond';
import {MAXFILESIZE, MAXTOTALFILESIZE} from '../../../../vehicleCertification/constants';
import {Actions} from '../../../../common/actions';
import DocumentsTabTechDetailsModal from './documentsTabTechDetailsModal';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileRename from 'filepond-plugin-file-rename';

registerPlugin(
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginFileRename,
);

const {saveFormAndCreateDoc, getWtsCourseDocs, deleteWtsCourseDoc, getWtsCourseDocFile, clearWtsCourseDocs} = Actions;

const CourseDocumentsTab = ({courseForm}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const courseDocs = useSelector((state) => state.wtsCourseDocuments.courseDocuments?.data ||
    state.wtsCourseDocuments.courseDocuments || []);
  const currentDocFile = useSelector((state) => state.wtsCourseDocuments.currentDocFile);
  const processedDocRef = useRef(null);
  const [files, setFiles] = useState({
    'studyDocument': null,
    'quiz': null,
    'preEvaluationForm': null,
  });
  const [showAdd, setShowAdd] = useState({
    'studyDocument': false,
    'quiz': false,
    'preEvaluationForm': false,
  });

  useEffect(() => {
    if (courseForm?.id) {
      dispatch(getWtsCourseDocs(courseForm.id));
    }
    return () => {
      dispatch(clearWtsCourseDocs());
    };
  }, [courseForm?.id]);

  useEffect(() => {
    if (
      currentDocFile?.answers?.fileBase64 &&
      currentDocFile?._id !== processedDocRef.current
    ) {
      processedDocRef.current = currentDocFile?._id;
      const byteChars = atob(currentDocFile.answers.fileBase64);
      const byteArray = new Uint8Array([...byteChars].map((c) => c.charCodeAt(0)));
      const blob = new Blob([byteArray], {'type': 'application/pdf'});
      window.open(URL.createObjectURL(blob));
    }
  }, [currentDocFile]);

  const handleShowAddComponent = (e, component) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAdd((prev) => ({...prev, [component]: !prev[component]}));
  };

  const getDocType = (category) => {
    const types = {
      'studyDocument': 'STUDY_MATERIAL',
      'quiz': 'QUIZ',
      'preEvaluationForm': 'PRE_EVALUATION',
    };
    return types[category] || 'OTHER';
  };

  const handleOpenDoc = (e, mongoId) => {
    e.preventDefault();
    processedDocRef.current = null;
    dispatch(getWtsCourseDocFile(mongoId));
  };

  const handleUpload = async(e, componentType) => {
    e.preventDefault();
    e.stopPropagation();
    const fileToUpload = files[componentType];
    if (!fileToUpload || !courseForm.id) {
      return;
    }
    const reader = new FileReader();
    reader.onload = async() => {
      const base64Content = reader.result.split(',')[1];
      const formData = new FormData();
      formData.append('targetCollection', 'wtsDocStorage');
      formData.append('type', 'WTS_DOC_STORAGE');
      formData.append('status', 'published');
      formData.append('generalInfo', JSON.stringify({
        'courseId': courseForm.id,
        'category': componentType,
        'fileName': fileToUpload.name,
        'uploadDate': new Date().toISOString(),
      }));
      formData.append('answers', JSON.stringify({
        'description': `Documento ${componentType} - Curso ${courseForm.id}`,
        'fileBase64': base64Content,
      }));
      dispatch(saveFormAndCreateDoc(formData, courseForm.id, componentType, fileToUpload.name, user));
      setShowAdd((prev) => ({...prev, [componentType]: false}));
      setFiles((prev) => ({...prev, [componentType]: null}));
    };
    reader.readAsDataURL(fileToUpload);
  };

  const handleDelete = (e, docId, mongoId) => {
    e.preventDefault();
    dispatch(deleteWtsCourseDoc(courseForm.id, docId, mongoId));
  };

  const renderSection = (title, type) => {
    const sectionDocs = courseDocs.filter((d) => d.doc_type === getDocType(type));
    return (
      <Grid.Column>
        <div className='flex middle'>
          <Header as='h4' className='m-0'>{title}</Header>
          <div className='ml'>
            <Button
              type='button'
              basic
              circular
              compact
              inverted
              className='p-0'
              onClick={(e) => handleShowAddComponent(e, type)}
              icon={<Icon link circular inverted size='small'
                name={showAdd[type] ? 'close' : 'add'}
                color={showAdd[type] ? 'red' : 'green'} />} />
          </div>
        </div>
        {showAdd[type] && (
          <Container fluid style={{'marginTop': '10px'}}>
            <FilePond
              maxFileSize={MAXFILESIZE}
              maxTotalFileSize={MAXTOTALFILESIZE}
              acceptedFileTypes={['application/pdf']}
              credits={false}
              onupdatefiles={(fileItems) => {
                if (fileItems.length > 0) {
                  setFiles((prev) => ({...prev, [type]: fileItems[0].file}));
                }
              }}
              allowMultiple={false}
              labelIdle='Arrastrar PDF o <span class="filepond--label-action">Navegar</span>'
            />
            <Container fluid as='div' className='flex end' style={{'marginBottom': '10px'}}>
              <Button
                type='button'
                color='blue'
                icon='upload'
                size='mini'
                content='Subir PDF'
                disabled={!files[type]}
                onClick={(e) => handleUpload(e, type)}
              />
            </Container>
            <Divider fitted />
          </Container>
        )}
        <Container fluid style={{'marginTop': '15px'}}>
          {sectionDocs.length > 0 ? (
            <List divided relaxed>
              {sectionDocs.map((doc, idx) => (
                <List.Item
                  key={idx}
                  style={{'display': 'flex', 'justifyContent': 'space-between',
                    'alignItems': 'center', 'width': '100%', 'padding': '6px 0'}}>
                  <div style={{'display': 'flex', 'alignItems': 'center', 'gap': '6px'}}>
                    <Icon name='file pdf' color='red' />
                    <List.Content>
                      <List.Header as='a' onClick={(e) => handleOpenDoc(e, doc.mongo_id)}>
                        {doc.file_name}
                      </List.Header>
                    </List.Content>
                  </div>
                  <div style={{'marginLeft': 'auto', 'paddingLeft': '20px'}}>
                    <Icon link name='trash' color='red' onClick={(e) => handleDelete(e, doc.id, doc.mongo_id)} />
                  </div>
                </List.Item>
              ))}
            </List>
          ) : (
            <div className='flex center middle'>
              <Icon name='info circle' color='blue' size='large' />
              <span style={{'fontSize': '0.9em', 'marginLeft': '5px'}}>Sin PDF cargados</span>
            </div>
          )}
        </Container>
      </Grid.Column>
    );
  };

  return (
    <Card raised fluid className='tab-card'>
      <Card.Content>
        <div style={{'display': 'flex', 'justifyContent': 'flex-end', 'marginBottom': '10px'}}>
          <DocumentsTabTechDetailsModal />
        </div>
        <Grid columns='equal' divided stackable>
          {renderSection('Documentos de Estudio', 'studyDocument')}
          {renderSection('Cuestionarios', 'quiz')}
          {courseForm.pre_evaluation_needed !== 0 &&
            renderSection('Formularios Pre Evaluación', 'preEvaluationForm')
          }
        </Grid>
      </Card.Content>
    </Card>
  );
};

export default CourseDocumentsTab;
